import React, { useMemo } from "react";
import * as d3 from "d3";
import { sankey, sankeyLinkHorizontal } from "d3-sankey";

function transformData(data) {
  const nodes = Array.from(
    new Set(data.flatMap((d) => [d.gene, d.pathway])),
    (name) => ({ name })
  );

  const index = new Map(nodes.map((d, i) => [d.name, i]));

  const links = data.map((d) => ({
    source: index.get(d.gene),
    target: index.get(d.pathway),
    value: 1,
  }));

  return { nodes, links };
}

function getFontSize(nodeHeight) {
  // Minimum font size is 10, maximum is 20
  // Adjust these values as you see fit
  const minSize = 10;
  const maxSize = 20;
  const size = Math.round(nodeHeight / 4);

  if (size < minSize) return minSize;
  if (size > maxSize) return maxSize;
  return size;
}

const SankeyDiagram = ({
  data,
  svgRef,
}) => {
  const width = 960;
  const nodePadding = Math.max(5, 500 / data.length);
  const height = nodePadding * data.length * 2;
  const color = useMemo(() => d3.scaleOrdinal(d3.schemeCategory10), []);

  const sankeyGenerator = sankey()
    .nodeWidth(20)
    .nodePadding(nodePadding)
    .extent([
      [1, 1],
      [width - 1, height - 5],
    ]);

  const transformedData = transformData(data);
  const { nodes, links } = sankeyGenerator(transformedData);

  return (
    <div ref={svgRef}>
      <svg width={width} height={height}>
        <g style={{ mixBlendMode: "multiply" }}>
          {nodes.map((node, i) => (
            <g key={node.name}>
              <rect
                x={node.x0}
                y={node.y0}
                height={node.y1 - node.y0}
                width={node.x1 - node.x0}
                fill={color(node.name)}
              >
                <title>{node.name}</title>
              </rect>
              <text
                x={node.x0 < width / 2 ? node.x1 + 6 : node.x0 - 6}
                y={(node.y1 + node.y0) / 2}
                fontSize={getFontSize(node.y1 - node.y0)}
                dy="0.35em"
                textAnchor={node.x0 < width / 2 ? "start" : "end"}
              >
                {node.name}
              </text>
            </g>
          ))}
          {links.map((link, i) => (
            <path
              d={sankeyLinkHorizontal()(link) ?? ""}
              style={{
                fill: "none",
                strokeOpacity: 0.45,
                stroke: color(link.source.name),
                strokeWidth: 12, // this fixes the height of the bands
              }}
            >
              <title>
                {link.source.name} → {link.target.name} {link.value}
              </title>
            </path>
          ))}
        </g>
      </svg>
    </div>
  );
};

export default SankeyDiagram;
